"use client";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { publicSans } from "@portal/app/fonts";
import { LandingPageBlock } from "../../components/LandingPageBlock";
import type { ResolvedPageContentBlock } from "../../types";
import type { PuckImageFrame } from "../types";
import { PAGE_DESKTOP_MEDIA } from "../../constants";
import { PUCK_TABLET_MEDIA } from "../layoutGeometry";

type ImageBlock = Extract<ResolvedPageContentBlock, { blockType: "image" }>;
export const DEFAULT_IMAGE_FRAME: Required<
  Omit<PuckImageFrame, "mobileCropHeight" | "mobileCropUntil">
> = {
  mobileHeight: 190,
  tabletHeight: 420,
  desktopHeight: 712,
  focalX: 50,
  focalY: 20,
  mobileFocalY: 20,
};

export function PageImage({
  block,
  frame = DEFAULT_IMAGE_FRAME,
  hideCaptionOnMobile = false,
  fixed = false,
}: {
  block: ImageBlock;
  frame?: PuckImageFrame;
  hideCaptionOnMobile?: boolean;
  fixed?: boolean;
}) {
  if (!fixed && !hideCaptionOnMobile) return <LandingPageBlock block={block} />;
  if (!fixed)
    return (
      <Box
        sx={{
          height: block.sizing === "fill" ? "100%" : undefined,
          ...(hideCaptionOnMobile
            ? { "& figcaption": { display: "none", [PUCK_TABLET_MEDIA]: { display: "flex" } } }
            : {}),
        }}
      >
        <LandingPageBlock block={block} />
      </Box>
    );
  const { image, caption } = block.resolvedContent;
  const resolvedFrame = {
    mobileHeight: frame.mobileHeight ?? DEFAULT_IMAGE_FRAME.mobileHeight,
    tabletHeight: frame.tabletHeight ?? DEFAULT_IMAGE_FRAME.tabletHeight,
    desktopHeight: frame.desktopHeight ?? DEFAULT_IMAGE_FRAME.desktopHeight,
    focalX: frame.focalX ?? DEFAULT_IMAGE_FRAME.focalX,
    focalY: frame.focalY ?? DEFAULT_IMAGE_FRAME.focalY,
  };
  const overlay = block.captionPlacement === "overlay";
  const cropUntil = frame.mobileCropUntil ?? 768;
  const croppedTablet = Boolean(frame.mobileCropHeight && cropUntil > 768);
  const cropReset = `@media (min-width: ${cropUntil}px)`;
  return (
    <Box component="figure" sx={{ m: 0, position: "relative" }}>
      <Box
        data-testid="landing-page-image-frame"
        sx={{
          position: "relative",
          overflow: "hidden",
          height: frame.mobileCropHeight
            ? Math.min(frame.mobileCropHeight, resolvedFrame.mobileHeight)
            : resolvedFrame.mobileHeight,
          borderRadius: block.radius === "rounded" ? "16px" : 0,
          [PUCK_TABLET_MEDIA]: {
            height: croppedTablet
              ? Math.min(frame.mobileCropHeight!, resolvedFrame.tabletHeight)
              : resolvedFrame.tabletHeight,
            borderRadius: block.radius === "rounded" ? "22px" : 0,
          },
          ...(croppedTablet ? { [cropReset]: { height: resolvedFrame.tabletHeight } } : {}),
          [PAGE_DESKTOP_MEDIA]: { height: resolvedFrame.desktopHeight },
        }}
      >
        <Box
          component="img"
          src={image.url}
          alt={image.alt}
          width={image.width}
          height={image.height}
          sx={{
            position: "absolute",
            inset: 0,
            display: "block",
            width: "100%",
            height: frame.mobileCropHeight ? resolvedFrame.mobileHeight : "100%",
            objectFit: block.fit,
            objectPosition: `${resolvedFrame.focalX}% ${frame.mobileFocalY ?? resolvedFrame.focalY}%`,
            [PUCK_TABLET_MEDIA]: {
              height: croppedTablet ? resolvedFrame.tabletHeight : "100%",
              objectPosition: `${resolvedFrame.focalX}% ${resolvedFrame.focalY}%`,
            },
            ...(croppedTablet ? { [cropReset]: { height: "100%" } } : {}),
            [PAGE_DESKTOP_MEDIA]: { height: "100%" },
          }}
        />
      </Box>
      {caption && (
        <Typography
          component="figcaption"
          sx={{
            display: hideCaptionOnMobile ? "none" : "flex",
            [PUCK_TABLET_MEDIA]: { display: "flex" },
            ...(overlay
              ? {
                  position: "absolute",
                  bottom: "18px",
                  left: "18px",
                  maxWidth: "calc(100% - 36px)",
                  maxHeight: "calc(100% - 36px)",
                  overflowY: "auto",
                  boxSizing: "border-box",
                  alignItems: "flex-start",
                  gap: "9px",
                  p: "9px 16px 9px 14px",
                  borderRadius: "20px",
                  bgcolor: "white",
                  color: "#0b2370",
                  fontFamily: publicSans.style.fontFamily,
                  fontSize: 15,
                  fontWeight: 600,
                  lineHeight: "28px",
                  m: 0,
                }
              : {
                  color: block.background === "brand" ? "white" : "text.primary",
                  mt: 1,
                  fontSize: 13,
                }),
          }}
        >
          {overlay && (
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                mt: "9px",
                borderRadius: "50%",
                bgcolor: "currentColor",
                flexShrink: 0,
              }}
            />
          )}
          <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
            {caption}
          </Box>
        </Typography>
      )}
    </Box>
  );
}
